// packages/shared/src/credit/types.ts
//
// Credit domain TS interfaces + Zod schemas — Phase 3.4b.
//
// Node-pure (no Fastify / Mongoose). Safe to bundle in Vite client builds.
//
// Per CONTEXT.md:
//   - Admin grants credit per merchant: limit (cents), period (days), startDate
//   - Options: notify, expiry reminder (fixed 7d + 1d), remindAtAmountCents,
//     autoLockOverdue, autoReset
//   - Lifecycle: active → overdue (hết hạn + còn nợ) → expired (hết hạn + không nợ)
//                or revoked (admin thu hồi)
//   - 1 active credit per merchant at a time (DB-layer enforced —)
//   - USD-only this phase (`currencyCode` literal `'USD'`)
// === Shared zod primitives — declared once, referenced by every block below ===
import { z } from 'zod';
export const ObjectIdHex = z.string().regex(/^[a-f0-9]{24}$/, 'Mongo ObjectId hex');
export const Iso4217 = z.string().length(3).regex(/^[A-Z]{3}$/);
export const UnixMs = z.number().int().nonnegative();
export const IsoDateTime = z.string().datetime({ offset: true }); // ISO-8601 with TZ
export const Int32Cents = z.number().int().nonnegative(); // monetary, in cents
export const PositiveCents = z.number().int().positive(); // strictly > 0
// === Credit lifecycle enum — 4 states ===
export const CreditStatus = z.enum(['active', 'overdue', 'expired', 'revoked']);
// === Credit options shape — 5 toggles ===
export const CreditOptions = z.object({
    notify: z.boolean().default(false),
    expiryReminderEnabled: z.boolean().default(true),
    remindAtAmountCents: PositiveCents.nullish(),
    autoLockOverdue: z.boolean().default(true),
    autoReset: z.boolean().default(false),
}).strict();
// === Credit sticky flags ===
export const CreditFlags = z.object({
    remindAtSent: z.boolean().default(false),
    expiry7dSent: z.boolean().default(false),
    expiry1dSent: z.boolean().default(false),
}).strict();
// === Credit base payload (response shape) ===
export const CreditPayload = z.object({
    creditId: ObjectIdHex,
    merchantId: ObjectIdHex,
    description: z.string().max(500).nullable(),
    limitCents: PositiveCents,
    periodDays: z.number().int().min(1).max(3650),
    startDate: UnixMs,
    endDate: UnixMs,
    status: CreditStatus,
    currencyCode: z.literal('USD'),
    options: CreditOptions,
    flags: CreditFlags,
    revokedAt: UnixMs.nullable(),
    revokedReason: z.string().nullable(),
    revokedByUserId: ObjectIdHex.nullable(),
    createdAt: UnixMs,
    updatedAt: UnixMs,
    createdByUserId: ObjectIdHex.nullable(),
    updatedByUserId: ObjectIdHex.nullable(),
}).strict();
//# sourceMappingURL=types.js.map